<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRepo } from 'pinia-orm';
import { UserStep } from 'modules/Step/models/UserStep';
import { useRouter } from 'vue-router';
import { useAssignments } from 'shared/composables/useAssignments';
import ListWrapperCard from 'shared/components/ListWrapperCard/ListWrapperCard.vue';
import BListFilterBar from 'src/shared/components/base/BListFilterBar/BListFilterBar.vue';
import {
  compareByListCriteria,
  matchesListCriteria,
  toListQueryParams,
  useListFilters,
} from 'shared/composables/useListFilters/useListFilters';
import type { QInfiniteScroll } from 'quasar';
import StepsList from '../../components/StepsList/StepsList.vue';
import { toFilterableStepItem } from '../../useStepData';

const router = useRouter();
const { fetchUserSteps, isLastStep, isLastFilteredStep, stepTotal } =
  useAssignments();

const userStepsRepo = useRepo(UserStep);

const {
  criteria,
  activeFilterCount,
  hideCompleted,
  sortBy,
  search,
  selectedStatuses,
  toggleStatus,
  clearAll,
} = useListFilters();

const criteriaActive = computed(
  () => activeFilterCount.value > 0 || hideCompleted.value,
);

// Steps before list criteria are applied — the "M" of the bar's "N / M".
const baseUserSteps = computed(() =>
  userStepsRepo.withAllRecursive().where('user_section_id', -1).get(),
);

// The list criteria are mirrored over the cache (same definitions the backend
// applies — see useListFilters) so the display reacts instantly and cached
// records from other pages can never leak into a filtered view.
const userSteps = computed(() => {
  if (!criteriaActive.value) return baseUserSteps.value;
  const current = criteria.value;
  return baseUserSteps.value
    .filter((userStep) => matchesListCriteria(toFilterableStepItem(userStep), current))
    .sort((a, b) =>
      compareByListCriteria(
        toFilterableStepItem(a),
        toFilterableStepItem(b),
        current.sortBy,
      ),
    );
});

// Criteria emptied a non-empty list — say so instead of showing nothing.
const showFilteredEmptyState = computed(
  () =>
    criteriaActive.value &&
    !userSteps.value.length &&
    baseUserSteps.value.length > 0,
);

const infiniteScrollRef = ref<QInfiniteScroll | null>(null);

function fetchWithCriteria() {
  // Empty status selection shows nothing — no point asking the server.
  if (criteria.value.statuses.length === 0) return Promise.resolve();
  return fetchUserSteps(undefined, toListQueryParams(criteria.value));
}

// Criteria changes repaginate server-side: the mirrored repo query already
// updated the visible list, this refetch fills page 1 of the new ordering and
// re-arms the infinite scroll for the rest.
watch(criteria, () => {
  void fetchWithCriteria().then(() => {
    infiniteScrollRef.value?.reset();
    infiniteScrollRef.value?.resume();
  });
});

function onLoad(index: number, done: (stop: boolean) => void): void {
  void index;
  void fetchWithCriteria();
  done(criteriaActive.value ? isLastFilteredStep.value : isLastStep.value);
}

function handleSelectedUserStep(userStep: UserStep) {
  const userStepId = userStep?.id;
  if (!userStepId) return;
  const stepDocId = userStep?.step?.documentId;
  if (!stepDocId) return;
  void router.push({
    name: 'step.drill',
    params: { userStepId, stepDocId },
  });
}
</script>

<template>
  <ListWrapperCard
    flat
    class="fit bg-transparent q-pa-none"
  >
    <BListFilterBar
      v-model:hide-completed="hideCompleted"
      v-model:sort-by="sortBy"
      v-model:search="search"
      :selected-statuses="selectedStatuses"
      :active-filter-count="activeFilterCount"
      :shown-count="userSteps.length"
      :total-count="stepTotal ?? baseUserSteps.length"
      @toggle:status="toggleStatus"
      @clear="clearAll"
    />
    <q-banner
      v-if="showFilteredEmptyState"
      class="text-center q-mt-lg bg-transparent"
    >
      <p class="q-mb-sm">{{ $t('NO_TASKS_MATCH_FILTERS') }}</p>
      <q-btn
        flat
        no-caps
        color="accent"
        :label="$t('CLEAR_ALL')"
        @click="clearAll"
      />
    </q-banner>
    <q-infinite-scroll
      v-else
      ref="infiniteScrollRef"
      :offset="150"
      @load="onLoad"
    >
      <StepsList
        :user-steps="userSteps"
        @slected-user-step="handleSelectedUserStep"
      />
      <template #loading>
        <div class="row justify-center q-pa-md">
          <q-spinner
            size="40px"
            color="primary"
          />
        </div>
      </template>
    </q-infinite-scroll>
  </ListWrapperCard>
</template>
